import type { GeoPt } from '../../lib/types';
import { MIN_RING_POINTS } from '../../lib/polygonUnion';
import { pointInArea } from '../../lib/geo';

// A property identified via a single tap on the cadastral overlay.
// Coordinates come from the user's click, designation from Tanger's
// property-info endpoint (the same data source as the Fastigheter raster
// overlay), so selection always matches the boundaries the user sees.
// `polygon` is the parcel's OUTER ring — its identity (the id is keyed on its
// centroid), the seed for the marker, and what a consumer that draws one ring
// uses. `rings` is the whole outline: the outer first, then every hole, which
// is another owner's land the parcel encloses. The two used to be the same
// thing because the service dropped the holes; a hunting ground built from
// the outer alone claimed land the property does not include. An older API
// still answers without `rings`, so it stays optional and {@link
// propertyRings} supplies the fallback.
export type PropertyFeature = {
  id: string;
  designation: string;
  lat: number;
  lon: number;
  polygon?: GeoPt[];
  rings?: GeoPt[][];
  /** Leading outers in `rings`. Absent means one — a parcel is one polygon with holes. */
  outerCount?: number;
};

/** Every ring of the parcel, outer first; `[polygon]` when the service sent no ring list. */
export function propertyRings(p: PropertyFeature): GeoPt[][] {
  if (p.rings && p.rings.length > 0) return p.rings;
  return p.polygon ? [p.polygon] : [];
}

/** How many of `propertyRings(p)` are outers. A parcel is one polygon with holes
 *  unless the service says otherwise. */
export function propertyOuterCount(p: PropertyFeature): number {
  return p.outerCount ?? 1;
}

// The three ways a property tap can resolve. `empty` and `error` are distinct
// on purpose: the Tanger proxy returns `{ property: null }` both for a tap
// over water and for a tap that simply hits no parcel, so a null property is
// a real (if empty) answer — not a failure — and must not be treated as one.
export type PropertyTapOutcome =
  | { kind: 'selected'; property: PropertyFeature }
  | { kind: 'empty' }
  | { kind: 'error' };

// The lookup for the parcel under a tapped point. The API parses `lat,lon` in
// that order.
export function propertyTraceUrl(lat: number, lon: number): string {
  return `/api/properties?trace=${lat.toFixed(6)},${lon.toFixed(6)}`;
}

// Classifies a `/api/properties?trace=` response body into a tap outcome.
// A missing body (null/undefined) means the request or parse failed; a present
// body with no `property` means the coordinate resolved to no selectable
// parcel; anything else is a hit.
export function classifyPropertyResponse(
  data: { property: PropertyFeature | null } | null | undefined,
): PropertyTapOutcome {
  if (data == null) return { kind: 'error' };
  if (data.property == null) return { kind: 'empty' };
  // A parcel without a usable ring (no polygon, or fewer than MIN_RING_POINTS
  // points) can't become a border/area: the same MIN_RING_POINTS filter drops
  // it when the selection is turned into a shape at save time. Accepting it as
  // a selection here would look like a successful tap that silently contributes
  // nothing, so treat it as the same "can't be added" outcome as no-parcel and
  // let the caller surface the notice instead.
  if (!data.property.polygon || data.property.polygon.length < MIN_RING_POINTS) {
    return { kind: 'empty' };
  }
  return { kind: 'selected', property: data.property };
}

// What tapping an identified parcel does to the current selection. `remove`
// carries the id to drop — which is NOT necessarily `hit.id` (see below).
export type PropertyTapAction = { action: 'remove'; id: string } | { action: 'add' };

// Decides whether a tapped parcel toggles an existing selection off or adds a
// new one, given the parcels currently selected.
//
// Two ways a tap can match something already selected:
//  1. Same id — an in-session re-tap of a parcel picked earlier this session.
//     Its Tanger id (`tanger:designation:lat,lon`) is stable within a session,
//     so equality resolves it directly.
//  2. Geometry — the parcel was SEEDED from a saved hunt border, which carries
//     a server-assigned id, not the ephemeral Tanger id. A fresh tap on that
//     same ground therefore arrives with a DIFFERENT id, so id equality can't
//     see it; the only durable link is that the tap's point falls inside the
//     seeded parcel's outline. Without this, re-tapping a saved parcel added a
//     duplicate instead of removing it.
//
// The geometry test honours holes via {@link pointInArea}: the land in a hole
// is a different parcel, and the property service answers a tap there with
// THAT parcel. Testing the outer ring alone would find the enclosing parcel
// and deselect it instead of adding the one that was actually tapped.
//
// Anything matching neither is a genuinely new pick and is added.
export function resolvePropertyTap(
  hit: PropertyFeature,
  selected: readonly PropertyFeature[],
): PropertyTapAction {
  if (selected.some((p) => p.id === hit.id)) return { action: 'remove', id: hit.id };
  const enclosing = selected.find((p) => {
    const rings = propertyRings(p);
    return rings.length > 0 && pointInArea({ lat: hit.lat, lon: hit.lon }, rings, propertyOuterCount(p));
  });
  if (enclosing) return { action: 'remove', id: enclosing.id };
  return { action: 'add' };
}
